/**
 * All date logic uses the family's timezone consistently.
 * Functions accept a timezone string parameter.
 */

/** Returns today's date string in YYYY-MM-DD format for the given timezone */
export function getDateString(date: Date = new Date(), timezone: string = "Europe/Oslo"): string {
  const parts = new Intl.DateTimeFormat("sv-SE", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);

  const y = parts.find((p) => p.type === "year")!.value;
  const m = parts.find((p) => p.type === "month")!.value;
  const d = parts.find((p) => p.type === "day")!.value;
  return `${y}-${m}-${d}`;
}

/** Format a date for display in Norwegian */
export function formatDate(date: Date | string, timezone: string = "Europe/Oslo", options?: Intl.DateTimeFormatOptions): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat("no-NO", {
    timeZone: timezone,
    ...options,
  }).format(d);
}

/** Format date as "mandag 3. mars" */
export function formatDateLong(date: Date | string, timezone: string = "Europe/Oslo"): string {
  return formatDate(date, timezone, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

/**
 * Child-friendly date display:
 * - "i dag" / "i morgen" for today/tomorrow
 * - "mandag" (bare ukedag) for resten av denne uken
 * - "neste mandag" for neste uke
 * - Etter det: "3. mars" (dato + måned)
 */
export function formatChildFriendlyDate(
  dateStr: string,
  timezone: string = "Europe/Oslo"
): string {
  const today = getDateString(new Date(), timezone);
  const todayDate = new Date(today + "T12:00:00");
  const targetDate = new Date(dateStr + "T12:00:00");

  const diffMs = targetDate.getTime() - todayDate.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "i dag";
  if (diffDays === 1) return "i morgen";
  if (diffDays < 0) return formatDate(dateStr, timezone, { weekday: "long", day: "numeric", month: "long" });

  // This week (2-6 days ahead, same Mon–Sun week or just close)
  if (diffDays <= 6) {
    return formatDate(dateStr, timezone, { weekday: "long" });
  }

  // Next week (7-13 days)
  if (diffDays <= 13) {
    const weekday = formatDate(dateStr, timezone, { weekday: "long" });
    return `neste ${weekday}`;
  }

  // Beyond: "3. mars"
  return formatDate(dateStr, timezone, { day: "numeric", month: "long" });
}

/** Get start-of-day and end-of-day ISO strings (DST-aware) for the given timezone */
export function getDayRange(date: Date = new Date(), timezone: string = "Europe/Oslo"): { start: string; end: string } {
  const dateStr = getDateString(date, timezone);
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    timeZoneName: "longOffset",
  });
  const parts = formatter.formatToParts(date);
  const offsetStr = parts.find((p) => p.type === "timeZoneName")?.value || "GMT+01:00";
  const offset = offsetStr.replace("GMT", "");
  return {
    start: `${dateStr}T00:00:00${offset}`,
    end: `${dateStr}T23:59:59${offset}`,
  };
}
